import styled from 'styled-components'
import { AxiosError } from 'axios'
import { BaseCell } from '../shared/BaseCell'
import { colors, colorSystem } from '@/styles/theme'
import { WarehouseSlotView } from '@/services/outboundSchedule2/types'
import { useCutOffHoverContext } from '../../context/CutOffHoverContext'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { Switch } from '@/components/content/Switch/Switch'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { useUpdateScheduleStatus } from '@/services/outboundSchedule2/useOutboundScheduleQueries'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { OUTBOUND_INSTRUCTION_TOGGLE_ENABLED } from '../../constants'

type Props = {
  data: WarehouseSlotView
  warehouseName: string
  startTime: string // HH:mm:ss
  endTime: string // HH:mm:ss
  isCurrent?: boolean
  isExpired?: boolean
  isColumnHover?: boolean
}

export function CutOffCell({ data, warehouseName, startTime, endTime, isCurrent = false, isExpired = false, isColumnHover = false }: Props) {
  const { priority, warehouseCode, isEnabled } = data
  const { isCutOffHover } = useCutOffHoverContext()
  const { cutOffDate, shipperCode } = useCutOffDate()
  const updateScheduleStatus = useUpdateScheduleStatus()

  // 출고지시 토글: 기능 플래그 ON + 지난/운영중(현재) 시간대 제외일 때만 노출
  const isToggleable = OUTBOUND_INSTRUCTION_TOGGLE_ENABLED && !isExpired && !isCurrent
  // 플래그 OFF면 출고지시 상태를 무시하고 항상 활성으로 표시
  const isActive = OUTBOUND_INSTRUCTION_TOGGLE_ENABLED ? isEnabled : true

  // priority가 설정된 센터는 숫자로 노출(합포순/센터순 무관), 미설정은 '합포'
  // (LLD §11: 센터순도 최대 5순위까지만 지정, 이후 순서는 합포 기준)
  const isPriorityNumber = priority != null
  const baseDisplayText = isPriorityNumber ? priority : '합포'
  const displayText = isActive ? baseDisplayText : '미지시'

  const baseTextColor = isCurrent
    ? colors.white
    : isExpired
      ? colorSystem.text.common.tertiary
      : isPriorityNumber
        ? 'rgba(0, 0, 0, 0.88)'
        : tokens['fg-lower']
  // off 상태(토글 가능 영역)면 dim 처리
  const textColor = isToggleable && !isActive ? colorSystem.text.common.disabled : baseTextColor

  const backgroundColor = isCurrent
    ? 'transparent'
    : isToggleable && !isActive
      ? colors.gray050
      : isCutOffHover || isColumnHover
        ? 'rgba(36, 94, 255, 0.04)'
        : 'transparent'

  const handleToggle = (next: boolean) => {
    updateScheduleStatus.mutate(
      { shipperCode, cutType: 'B2C', warehouseCode, startTime, endTime, isEnabled: next, applyDate: cutOffDate },
      {
        onSuccess: () => {
          const timeRange = `${startTime.slice(0, 5)}~${endTime.slice(0, 5)}`
          MToast.show(`${warehouseName} 센터 ${timeRange} 출고지시가 ${next ? '활성화' : '비활성화'}되었습니다.`)
        },
        // 낙관적 업데이트 롤백은 useUpdateScheduleStatus 의 onError 가 맡는다 — 여기서는 안내만 가른다
        onError: (error) => {
          MAlert.show(getShipperScopeErrorMessage(error) ?? Utils.getApiErrMessage(error as AxiosError<ApiResponseV2>, '출고지시 상태 변경에 실패했습니다.'))
        },
      },
    )
  }

  return (
    <Wrap style={{ backgroundColor }}>
      <PriorityText style={{ color: textColor, fontSize: isCurrent ? 14 : 12 }}>{displayText}</PriorityText>
      {isToggleable && (
        <SwitchWrap className="cell-switch">
          <Switch size="sm" checked={isActive} onCheckedChange={handleToggle} />
        </SwitchWrap>
      )}
    </Wrap>
  )
}

const Wrap = styled(BaseCell)`
  flex: 1;
  position: relative;
  justify-content: center;
  align-items: center;

  .cell-switch {
    display: none;
  }

  &:hover .cell-switch {
    display: flex;
  }
`

const PriorityText = styled.p`
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`

const SwitchWrap = styled.div`
  position: absolute;
  right: 12px;
  align-items: center;

  input:checked + label {
    background-color: ${tokens.layerDesktop['bg-accent']};
  }
`
